import { Host, forceUpdate, h } from "@stencil/core";
import { getWidgetDropInfo, handleWidgetDrag, removeDroppableAreaStyles } from "../utils";
import { getViewInfo } from "../../renders/flexible-layout/utils";
// Keys
const ESCAPE_KEY = "Escape";
// const KEY_B = "KeyB";
export class ChFlexibleLayout {
    constructor() {
        this.dragBarDisabled = false;
        this.layoutModel = undefined;
        this.layoutSplitterParts = undefined;
        this.itemsInfo = undefined;
    }
    #draggableViews;
    #dragInfo;
    #viewsOutOfDroppableZoneController; // Allocated at runtime to reduce memory usage
    // Refs
    #draggedViewRef;
    #droppableAreaRef;
    #layoutSplitterRef;
    el;
    /**
     * Fired when a item of a view request to be closed.
     */
    viewItemClose;
    /**
     * Fired when the selected item change.
     */
    selectedViewItemChange;
    /**
     * Fired when a widget is dragged and dropped into a view.
     */
    viewItemReorder;
    /**
     * Schedules a new render of the control even if no state changed.
     */
    async refreshLayout() {
        forceUpdate(this);
        this.#layoutSplitterRef.refreshLayout();
    }
    /**
     * Removes the view that is identified by the given ID.
     * The layout is rearranged depending on the state of the removed view.
     */
    async removeView(itemId) {
        const result = await this.#layoutSplitterRef.removeItem(itemId);
        if (result.success) {
            // Queue re-renders
            forceUpdate(this);
        }
        return result;
    }
    /**
     * Given the view ID and the item id, remove the page of the item from the view.
     */
    async removeItemPageInView(viewId, itemId, forceRerender = true) {
        const viewInfo = this.#getViewInfo(viewId);
        if (!viewInfo) {
            return;
        }
        const viewRef = this.el.shadowRoot.querySelector(`ch-tab[id='${viewInfo.id}']`);
        await viewRef.removePage(itemId, forceRerender);
    }
    #getViewInfo = (viewId) => getViewInfo(this.itemsInfo, viewId);
    #getAllViews = () => {
        const views = [];
        this.itemsInfo.forEach(item => {
            const itemView = item
                .view;
            if (itemView != null) {
                views.push(itemView);
            }
        });
        return views;
    };
    // @Listen("keydown", { target: "document" })
    // handleKeyDownEvent(event: KeyboardEvent) {
    // if (
    //   !mouseEventModifierKey(event) ||
    //   event.code !== KEY_B ||
    //   this.layout.inlineStart == null
    // ) {
    //   return;
    // }
    // event.stopPropagation();
    // event.preventDefault();
    // this.layout.inlineStart.expanded = !(
    //   this.layout.inlineStart.expanded ?? true
    // );
    //   forceUpdate(this);
    // }
    handleMainGroupExpand = () => {
        // if (this.layout.inlineStart) {
        //   this.layout.inlineStart.expanded = false;
        // }
        // if (this.layout.inlineEnd) {
        //   this.layout.inlineEnd.expanded = false;
        // }
        // if (this.layout.blockEnd) {
        //   this.layout.blockEnd.expanded = false;
        // }
        forceUpdate(this);
    };
    handleItemChange = (viewId) => (event) => {
        event.stopPropagation();
        // Add the view id to properly update the render
        const eventInfo = {
            ...event.detail,
            viewId: viewId
        };
        this.selectedViewItemChange.emit(eventInfo);
    };
    handleItemClose = (viewId) => (event) => {
        event.stopPropagation();
        // Add the view id to properly update the render
        const eventInfo = {
            ...event.detail,
            viewId: viewId
        };
        this.viewItemClose.emit(eventInfo);
    };
    handleDragStart = (viewId) => async (event) => {
        event.stopPropagation();
        // We MUST store the reference before the Promise.allSettle, otherwise
        // the event target will be the flexible-layout control
        this.#draggedViewRef = event.target;
        const views = [...this.el.shadowRoot.querySelectorAll("ch-tab")];
        this.#dragInfo = {
            index: event.detail,
            viewId: viewId
        };
        // Get all draggable views
        const draggableViewsResult = await Promise.allSettled(views.map(view => view.getDraggableViews()));
        // Allocate memory
        this.#draggableViews = [];
        // Add handlers to manage droppable areas
        draggableViewsResult.forEach((draggableViewResult, index) => {
            if (draggableViewResult.status === "fulfilled") {
                const draggableView = draggableViewResult.value;
                const abortController = new AbortController(); // Necessary to remove the event listener
                const extendedDraggableView = {
                    ...draggableView,
                    viewId: views[index].id,
                    abortController: abortController
                };
                this.#draggableViews.push(extendedDraggableView);
                draggableView.mainView.addEventListener("mousemove", handleWidgetDrag(extendedDraggableView, this.#droppableAreaRef), { capture: true, passive: true, signal: abortController.signal });
                // Remove pointer events to not interfere on the mousemove event
                extendedDraggableView.tabListView.style.pointerEvents = "none";
                extendedDraggableView.pageView.style.pointerEvents = "none";
            }
        });
        document.addEventListener("mouseup", this.#handleWidgetDragEnd, {
            passive: true
        });
        document.addEventListener("keydown", this.#handleWidgetDragEndKeydown, {
            passive: true
        });
        // Removes view when they are out of a droppable area
        this.#viewsOutOfDroppableZoneController = new AbortController();
        document.addEventListener("mousemove", () => removeDroppableAreaStyles(this.#droppableAreaRef), {
            passive: true,
            signal: this.#viewsOutOfDroppableZoneController.signal
        });
        // Show droppable area
        this.#droppableAreaRef.showPopover(); // Layer 1
        // After that, promote the drag preview to the second layer
        this.#draggedViewRef.promoteDragPreviewToTopLayer(); // Layer 2
        // Disable drag bars in layout-splitter to improve the drag experience
        this.dragBarDisabled = true;
    };
    #handleWidgetDragEndKeydown = (event) => {
        if (event.code !== ESCAPE_KEY) {
            return;
        }
        event.preventDefault();
        // Cancels the drop by removing the drop info
        removeDroppableAreaStyles(this.#droppableAreaRef);
        this.#handleWidgetDragEnd();
        this.#draggedViewRef.endDragPreview();
    };
    #handleWidgetDragEnd = () => {
        // Remove mousemove handlers
        this.#draggableViews.forEach(draggableView => {
            draggableView.abortController.abort();
            // Reset pointer events
            draggableView.tabListView.style.pointerEvents = null;
            draggableView.pageView.style.pointerEvents = null;
        });
        // Remove mouseup and keydown handlers
        document.removeEventListener("mouseup", this.#handleWidgetDragEnd);
        document.removeEventListener("keydown", this.#handleWidgetDragEndKeydown);
        this.#viewsOutOfDroppableZoneController.abort();
        // Check if must update the view due to a drop
        const dropInfo = getWidgetDropInfo();
        if (dropInfo) {
            this.viewItemReorder.emit({ ...this.#dragInfo, ...dropInfo });
        }
        // Hide droppable area
        this.#droppableAreaRef.hidePopover();
        removeDroppableAreaStyles(this.#droppableAreaRef);
        // Free the memory
        this.#draggableViews = undefined;
        this.#dragInfo = undefined;
        // Re-enable drag bars
        this.dragBarDisabled = false;
    };
    renderTab = (tabType, viewInfo) => (h("ch-tab", { id: viewInfo.id, key: viewInfo.id, slot: viewInfo.id, exportparts: viewInfo.exportParts, items: viewInfo.widgets, selectedId: viewInfo.selectedWidgetId, type: tabType, onExpandMainGroup: tabType === "main" ? this.handleMainGroupExpand : null, onItemClose: this.handleItemClose(viewInfo.id), onItemDragStart: this.handleDragStart(viewInfo.id), onSelectedItemChange: this.handleItemChange(viewInfo.id) }, viewInfo.widgets.map(widget => widget.wasRendered && h("slot", { name: widget.id, slot: widget.id }))));
    renderView = (view) => view.type === "blockStart" ? (h("header", { key: view.id, slot: view.id }, h("slot", null))) : (this.renderTab(view.type, view));
    render() {
        const layoutModel = this.layoutModel;
        if (layoutModel == null) {
            return "";
        }
        return (h(Host, null, h("ch-layout-splitter", { dragBarDisabled: this.dragBarDisabled, layout: layoutModel, exportparts: "bar," + this.layoutSplitterParts, ref: el => (this.#layoutSplitterRef = el) }, this.#getAllViews().map(this.renderView)), h("div", { "aria-hidden": "true", class: "droppable-area", part: "droppable-area", popover: "manual", ref: el => (this.#droppableAreaRef = el) })));
    }
    static get is() { return "ch-flexible-layout"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["flexible-layout.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["flexible-layout.css"]
        };
    }
    static get properties() {
        return {
            "layoutModel": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "LayoutSplitterDistribution",
                    "resolved": "{ id: \"root\"; direction: LayoutSplitterDirection; items: LayoutSplitterDistributionItem[]; }",
                    "references": {
                        "LayoutSplitterDistribution": {
                            "location": "import",
                            "path": "../../layout-splitter/types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterDistribution"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the distribution of the items in the flexible layout."
                }
            },
            "layoutSplitterParts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies additional parts to export."
                },
                "attribute": "layout-splitter-parts",
                "reflect": false
            },
            "itemsInfo": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Map<\n    string,\n    FlexibleLayoutItemExtended<FlexibleLayoutItem>\n  >",
                    "resolved": "Map<string, FlexibleLayoutItemExtended<FlexibleLayoutItem>>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "FlexibleLayoutItemExtended": {
                            "location": "import",
                            "path": "../types",
                            "id": "src/components/flexible-layout/types.ts::FlexibleLayoutItemExtended"
                        },
                        "FlexibleLayoutItem": {
                            "location": "import",
                            "path": "../types",
                            "id": "src/components/flexible-layout/types.ts::FlexibleLayoutItem"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the information of each view displayed."
                }
            }
        };
    }
    static get states() {
        return {
            "dragBarDisabled": {}
        };
    }
    static get events() {
        return [{
                "method": "viewItemClose",
                "name": "viewItemClose",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a item of a view request to be closed."
                },
                "complexType": {
                    "original": "ViewItemCloseInfo",
                    "resolved": "{ itemId: string; itemIndex: number; type: TabType; viewId: string; }",
                    "references": {
                        "ViewItemCloseInfo": {
                            "location": "import",
                            "path": "../types",
                            "id": "src/components/flexible-layout/types.ts::ViewItemCloseInfo"
                        }
                    }
                }
            }, {
                "method": "selectedViewItemChange",
                "name": "selectedViewItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected item change."
                },
                "complexType": {
                    "original": "ViewSelectedItemInfo",
                    "resolved": "{ lastSelectedIndex: number; newSelectedId: string; newSelectedIndex: number; type: TabType; viewId: string; }",
                    "references": {
                        "ViewSelectedItemInfo": {
                            "location": "import",
                            "path": "../types",
                            "id": "src/components/flexible-layout/types.ts::ViewSelectedItemInfo"
                        }
                    }
                }
            }, {
                "method": "viewItemReorder",
                "name": "viewItemReorder",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a widget is dragged and dropped into a view."
                },
                "complexType": {
                    "original": "WidgetReorderInfo",
                    "resolved": "WidgetDragInfo & WidgetDropInfo",
                    "references": {
                        "WidgetReorderInfo": {
                            "location": "import",
                            "path": "../types",
                            "id": "src/components/flexible-layout/types.ts::WidgetReorderInfo"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "refreshLayout": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Schedules a new render of the control even if no state changed.",
                    "tags": []
                }
            },
            "removeView": {
                "complexType": {
                    "signature": "(itemId: string) => Promise<LayoutSplitterItemRemoveResult>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "LayoutSplitterItemRemoveResult": {
                            "location": "import",
                            "path": "../../layout-splitter/types",
                            "id": "src/components/layout-splitter/types.ts::LayoutSplitterItemRemoveResult"
                        }
                    },
                    "return": "Promise<LayoutSplitterItemRemoveResult>"
                },
                "docs": {
                    "text": "Removes the view that is identified by the given ID.\nThe layout is rearranged depending on the state of the removed view.",
                    "tags": []
                }
            },
            "removeItemPageInView": {
                "complexType": {
                    "signature": "(viewId: string, itemId: string, forceRerender?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "viewId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "forceRerender",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTabElement": {
                            "location": "global",
                            "id": "global::HTMLChTabElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given the view ID and the item id, remove the page of the item from the view.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
}
//# sourceMappingURL=flexible-layout.js.map
